<!--
    ローディングアニメーションコンポーネント

    ※ ListUtilコンポーネントを使う場合はその中にすでに含まれているので、ListUtilコンポーネントの
       startLoading(), stopLoading() で使用できます。

    使用方法
    １．エレメントを定義します。
        <Loading ref="xxx" name="yyy" />

        ・ref="xxx"で参照名を指定。(必須)
        ・name='yyy'で識別名を指定。(省略可能ですが、１画面に２つ以上使う場合は必須)

    ２．親要エレメントのcss で
        position: relative;
        を指定してください。
        また、親要素の一番下に配置するとその上の要素の上に描画され、透過されます。

    ３．ローディング開始・終了はJavascript で参照からメソッドを実行
        【開始】
        const refObj: any = this.$refs;
        refObj.loading.startLoading();

        【終了】
        const refObj: any = this.$refs;
        refObj.loading.stopLoading();
-->
<template>
    <div :id="id" class="loading" :style="{height:strHeight, width:strWidth}">
        <div class="loader">
            <div class="loader-one"></div>
            <div class="loader-two"></div>
            <div class="loader-three"></div>
            <div class="loader-four"></div>
        </div>
    </div>
</template>

<script lang="ts">
    import { Options, Vue } from "vue-class-component";
    import { debounce } from 'lodash';

    @Options({
        props: {
            name: String
        },
    })


    /**
        * @brief	ローディングコンポーネント
        * @return	なし
        */
    export default class Loading extends Vue {
        private strHeight = "0px";
        private strWidth = "0px";
        private funcResize: any = null;
        private nTimerID: number | undefined = undefined;
        private name!: string;
        private id = "loading";
        static _nextID = 0;

        public created(): void {
            this.id = "loading_" + (this.name ?? (++Loading._nextID).toString());
        }

        public mounted(): void {

            let element: HTMLElement = document.getElementById(this.id) as HTMLElement;
            if (null != element) {
                element.style.display = 'none';
            }
        }

        /**
         * @brief	画面サイズを計算
         */
        public calculateSize(): void {
            if (null == this.$el.parentElement || undefined == this.$el.parentElement) {
                return;
            }
            if (null != this.$el.parentElement.clientHeight && undefined != this.$el.parentElement.clientHeight) {
                this.strHeight = (this.$el.parentElement.clientHeight) + 'px';    // 親の高さを取得する。
            }
            if (null != this.$el.parentElement.clientWidth && undefined != this.$el.parentElement.clientWidth) {
                this.strWidth = this.$el.parentElement.clientWidth + 'px';      // 親の幅を取得する。
            }
        }

        /**
         * @brief	タイマーイベント
         */
        public onTimerEvent(): void {
            // サイズを再計算
            this.calculateSize();
            this.nTimerID = undefined;
        }

        /**
         * @brief	画面サイズの変更イベント
         */
        public onResizeEvent(): void {
            this.calculateSize();

            // サイズを小さくしたとき、右側が足りないことがある（おそらく一瞬表示されるスクロールバーを計算に含んでいる。スクロールバーの削除時は"resize"イベントは発火されないので、0.3秒後に再計算する）
            if (undefined != this.nTimerID) {
                // 最後のリサイズイベントから1秒後にイベントを発火させたいので、古いのはキャンセル
                clearTimeout(this.nTimerID);
            }
            this.nTimerID = window.setTimeout(this.onTimerEvent, 300);
        }

        /**
         * @brief	ローディング表示開始
         * @return	なし
         */
        public startLoading(): void {
            let element: HTMLElement = document.getElementById(this.id) as HTMLElement;
            if (null != element) {
                element.style.display = 'flex';
                element.style.color = 'crimson';
            }

            // 画面サイズを計算
            this.calculateSize();

            // リサイズのイベント時にサイズを再計算する
            // ※debounceでリサイズの負荷を軽減
            if (null == this.funcResize) {
                this.funcResize = debounce(this.onResizeEvent, 100);      // イベント削除時はインスタンスを合わせておかないと削除できない
                window.addEventListener('resize', this.funcResize);
            }
        }

        /**
         * @brief	ローディング表示終了
         * @return	なし
         */
        public stopLoading(): void {
            let element: HTMLElement = document.getElementById(this.id) as HTMLElement;
            if (null != element) {
                element.style.display = 'none';
            }

            // リサイズイベントを削除
            if (null != this.funcResize) {
                window.removeEventListener('resize', this.funcResize);
                this.funcResize = null;
            }

            // タイマーが残っていたら削除
            if (undefined != this.nTimerID) {
                clearTimeout(this.nTimerID);
                this.nTimerID = undefined;
            }
        }
    }
</script>

<style scoped>

    .loading {
        background: rgba(20, 100, 185, 0.05);
        justify-content: center;
        align-items: center;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 2147483646;
    }

    .loader {
        position: absolute;
        width: 48px;
        height: 48px;
    }

        .loader div {
            position: absolute;
            width: 100%;
            height: 100%;
            border-radius: 50%;
            box-sizing: border-box;
            opacity: .8;
        }

    .loader-one {
        border-top: 2px solid #1464B9;
        animation: rotate-left 1.5s linear infinite;
    }

    .loader-two {
        border-right: 2px solid #05A0F0;
        animation: rotate-right 1.5s linear infinite;
    }

    .loader-three {
        border-bottom: 2px solid #A5E1FF;
        animation: rotate-right 1.5s linear infinite;
    }

    .loader-four {
        border-left: 2px solid #1464B9;
        animation: rotate-right 1.5s linear infinite;
    }

    @keyframes rotate-left {
        0% {
            transform: rotate(360deg);
        }

        100% {
            transform: rotate(0deg);
        }
    }

    @keyframes rotate-right {
        0% {
            transform: rotate(0deg);
        }

        100% {
            transform: rotate(360deg);
        }
    }
</style>